import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  useTheme,
  alpha,
  Icon,
  useMediaQuery,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Lottie from "react-lottie";
import dashboardAnimation from "../../../../assets/illustrations/server-upload.json";

// Background Pattern Components
const GridPattern = () => (
  <Box
    sx={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `
        linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
      `,
      backgroundSize: "64px 64px",
      opacity: 0.3,
      zIndex: 0,
    }}
  />
);

const DotsPattern = () => (
  <Box
    sx={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `radial-gradient(circle at center, rgba(255, 255, 255, 0.1) 2px, transparent 2px)`,
      backgroundSize: "32px 32px",
      opacity: 0.3,
      zIndex: 0,
    }}
  />
);

const CircleGlow = () => (
  <Box
    sx={{
      position: "absolute",
      width: "60%",
      paddingBottom: "60%",
      background:
        "radial-gradient(circle at center, rgba(6, 182, 212, 0.15) 0%, transparent 70%)",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      zIndex: 0,
      animation: "pulse 8s ease-in-out infinite",
      "@keyframes pulse": {
        "0%": {
          transform: "translate(-50%, -50%) scale(1)",
          opacity: 0.5,
        },
        "50%": {
          transform: "translate(-50%, -50%) scale(1.2)",
          opacity: 0.3,
        },
        "100%": {
          transform: "translate(-50%, -50%) scale(1)",
          opacity: 0.5,
        },
      },
    }}
  />
);

const teamRoles = [
  {
    role: "System Architects",
    description: "Building the foundation of seamless operations",
    icon: "architecture",
    gradient: "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
  },
  {
    role: "UX Researchers",
    description: "Understanding and solving real user needs",
    icon: "psychology",
    gradient: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
  },
  {
    role: "Developers",
    description: "Crafting powerful, efficient solutions",
    icon: "code",
    gradient: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
  },
  {
    role: "Designers",
    description: "Creating intuitive, beautiful experiences",
    icon: "palette",
    gradient: "linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)",
  },
];

const milestones = [
  {
    phase: "Phase 1",
    title: "Vision & Foundation",
    description:
      "Launched with a revolutionary vision to transform restaurant management through smart technology. Started with essential POS and inventory management features.",
    icon: "rocket_launch",
    highlight: "Core Features Launch",
    gradient: "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
  },
  {
    phase: "Phase 2",
    title: "Smart Operations",
    description:
      "Introduced intelligent inventory tracking, cost management, and recipe building capabilities. Empowering restaurants with data-driven decisions.",
    icon: "inventory",
    highlight: "Intelligent Systems",
    gradient: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
  },
  {
    phase: "Phase 3",
    title: "Multi-Branch Excellence",
    description:
      "Expanded capabilities with multi-location management, centralized control, and real-time synchronization across branches.",
    icon: "account_tree",
    highlight: "Branch Network",
    gradient: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
  },
  {
    phase: "Phase 4",
    title: "AI Innovation",
    description:
      "Revolutionizing operations with AI-powered inventory predictions, smart scheduling, and automated cost optimization for maximum efficiency.",
    icon: "psychology_alt",
    highlight: "Future Ready",
    gradient: "linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)",
  },
];

interface RoleCardProps {
  role: string;
  description: string;
  icon: string;
  gradient: string;
  index: number;
}

interface TimelineItemProps {
  phase: string;
  title: string;
  description: string;
  icon: string;
  highlight: string;
  gradient: string;
  index: number;
}

const RoleCard: React.FC<RoleCardProps> = ({
  role,
  description,
  icon,
  gradient,
  index,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    viewport={{ once: true }}
  >
    <Box
      sx={{
        p: 3,
        borderRadius: "24px",
        background: (theme) => alpha(theme.palette.background.paper, 0.8),
        backdropFilter: "blur(12px)",
        border: "1px solid",
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.1),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: 2,
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        cursor: "pointer",
        "&:hover": {
          transform: "translateY(-8px) scale(1.02)",
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
          "& .icon-container": {
            transform: "scale(1.1)",
          },
        },
      }}
    >
      <Box
        className="icon-container"
        sx={{
          width: 80,
          height: 80,
          borderRadius: "20px",
          background: gradient,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2,
          transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <Icon sx={{ color: "white", fontSize: 36 }}>{icon}</Icon>
      </Box>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          fontSize: "1.5rem",
          fontFamily: "Montserrat",
          background: gradient,
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          mb: 1,
        }}
      >
        {role}
      </Typography>
      <Typography
        sx={{
          color: "text.secondary",
          fontSize: "1rem",
          lineHeight: 1.6,
          fontFamily: "Montserrat",
        }}
      >
        {description}
      </Typography>
    </Box>
  </motion.div>
);

const TimelineItem: React.FC<TimelineItemProps> = ({
  phase,
  title,
  description,
  icon,
  highlight,
  gradient,
  index,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: index * 0.2 }}
    viewport={{ once: true }}
  >
    <Box
      sx={{
        display: "flex",
        gap: 3,
        position: "relative",
        mb: 8,
        "&::before": {
          content: '""',
          position: "absolute",
          left: 28,
          top: 60,
          bottom: -60,
          width: 3,
          background: (theme) => `linear-gradient(180deg, 
            ${alpha(gradient.split(", ")[1].split(" ")[0], 0.5)} 0%,
            ${alpha(gradient.split(", ")[2].split(")")[0], 0.1)} 100%)`,
          display: index === milestones.length - 1 ? "none" : "block",
          borderRadius: "4px",
        },
      }}
    >
      <Box sx={{ position: "relative" }}>
        <Box
          sx={{
            background: gradient,
            borderRadius: "16px",
            width: 56,
            height: 56,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            position: "relative",
            zIndex: 2,
            boxShadow: `0 8px 16px ${alpha(
              gradient.split(", ")[1].split(" ")[0],
              0.25
            )}`,
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "scale(1.1)",
              boxShadow: `0 12px 24px ${alpha(
                gradient.split(", ")[1].split(" ")[0],
                0.35
              )}`,
            },
          }}
        >
          <Icon sx={{ color: "white", fontSize: 28 }}>{icon}</Icon>
        </Box>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              color: "text.secondary",
              fontWeight: 600,
              fontSize: "0.875rem",
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              fontFamily: "Montserrat",
              background: gradient,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {phase}
          </Typography>
          <Box
            sx={{
              px: 2,
              py: 0.5,
              borderRadius: "full",
              background: alpha(gradient.split(", ")[1].split(" ")[0], 0.1),
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 600,
                color: gradient.split(", ")[1].split(" ")[0],
              }}
            >
              {highlight}
            </Typography>
          </Box>
        </Box>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            mb: 1,
            fontFamily: "Montserrat",
            background: gradient,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            color: "text.secondary",
            fontSize: "1rem",
            lineHeight: 1.6,
            maxWidth: "600px",
            fontFamily: "Montserrat",
          }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  </motion.div>
);

export const About = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box sx={{ overflow: "hidden" }}>
      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 12, md: 20 },
          pb: { xs: 10, md: 16 },
          background: "linear-gradient(135deg, #000B18 0%, #001E3C 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <GridPattern />
        <DotsPattern />
        <CircleGlow />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.4,
            background:
              "radial-gradient(circle at 20% 20%, rgba(6, 182, 212, 0.15) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgba(59, 130, 246, 0.15) 0%, transparent 40%)",
            zIndex: 0,
            animation: "float 15s ease-in-out infinite",
            "@keyframes float": {
              "0%": {
                transform: "translateY(0) scale(1)",
              },
              "50%": {
                transform: "translateY(-20px) scale(1.05)",
              },
              "100%": {
                transform: "translateY(0) scale(1)",
              },
            },
          }}
        />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <Typography
              variant="h1"
              align="center"
              sx={{
                fontSize: { xs: "2.5rem", md: "4rem" },
                fontWeight: 800,
                mb: 3,
                color: "white",
                lineHeight: 1.2,
                maxWidth: "1000px",
                mx: "auto",
                fontFamily: "Montserrat",
                textShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
              }}
            >
              Revolutionizing Restaurant Management
              <Box
                component="span"
                sx={{
                  display: "block",
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 600,
                  mt: 2,
                  fontFamily: "Montserrat",
                  background:
                    "linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                One Innovation at a Time
              </Box>
            </Typography>
            <Typography
              variant="h5"
              align="center"
              sx={{
                maxWidth: "800px",
                mx: "auto",
                mb: 6,
                color: "rgba(255, 255, 255, 0.95)",
                fontSize: { xs: "1.1rem", md: "1.25rem" },
                lineHeight: 1.8,
                fontFamily: "Montserrat",
                textShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              }}
            >
              VenFlows started with one mission: to make business simpler,
              smarter, and more seamless—starting from the heart of it all:
              restaurants.
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 3,
                mb: 8,
              }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate("/auth/register")}
                sx={{
                  background: "linear-gradient(90deg, #06B6D4, #0891B2)",
                  color: "white",
                  px: 4,
                  py: 2,
                  fontSize: "1.1rem",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: "0 8px 24px rgba(6, 182, 212, 0.25)",
                  "&:hover": {
                    background: "linear-gradient(90deg, #0891B2, #06B6D4)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 32px rgba(6, 182, 212, 0.35)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Get Started Now
              </Button>
            </Box>
            {/* <Box
              sx={{
                maxWidth: "1000px",
                mx: "auto",
                borderRadius: "24px",
                overflow: "hidden",
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.2)",
                background: "rgba(255, 255, 255, 0.05)",
                backdropFilter: "blur(12px)",
                p: 2,
              }}
            >
              <Lottie
                options={{
                  animationData: dashboardAnimation,
                  loop: true,
                  autoplay: true,
                  rendererSettings: {
                    preserveAspectRatio: "xMidYMid slice",
                  },
                }}
                height={400}
                width={isMobile ? "100%" : 900}
              />
            </Box> */}
          </motion.div>
        </Container>
      </Box>

      {/* Team Section */}
      <Box
        sx={{
          py: { xs: 10, md: 16 },
          background: "white",
          position: "relative",
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h2"
              align="center"
              sx={{
                fontSize: { xs: "2rem", md: "3rem" },
                fontWeight: 800,
                mb: 2,
                background: "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Meet the Dream Team
            </Typography>
            <Typography
              align="center"
              sx={{
                color: "text.secondary",
                fontSize: { xs: "1.1rem", md: "1.25rem" },
                lineHeight: 1.7,
                mb: 8,
                maxWidth: "800px",
                mx: "auto",
              }}
            >
              We're a diverse team of thinkers, creators, and problem-solvers,
              united by our passion for revolutionizing business operations.
            </Typography>
          </motion.div>
          <Grid container spacing={4}>
            {teamRoles.map((role, index) => (
              <Grid item xs={12} sm={6} md={3} key={role.role}>
                <RoleCard {...role} index={index} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Who We Are Section */}
      <Box
        sx={{
          py: { xs: 10, md: 16 },
          background: "white",
          position: "relative",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "2rem", md: "3rem" },
                    fontWeight: 800,
                    mb: 4,
                    background:
                      "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Who We Are
                </Typography>
                <Typography
                  sx={{
                    color: "text.secondary",
                    fontSize: { xs: "1.1rem", md: "1.25rem" },
                    lineHeight: 1.7,
                    mb: 4,
                  }}
                >
                  VenFlows is more than just a restaurant management system -
                  we're a team of industry experts, technologists, and
                  innovators dedicated to transforming how restaurants operate.
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      color: "primary.main",
                    }}
                  >
                    Our Mission
                  </Typography>
                  <Typography
                    sx={{
                      color: "text.secondary",
                      fontSize: "1.1rem",
                      lineHeight: 1.7,
                      mb: 3,
                    }}
                  >
                    To empower restaurants with intelligent, user-friendly
                    technology that simplifies operations, reduces costs, and
                    drives growth.
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      color: "primary.main",
                    }}
                  >
                    Our Approach
                  </Typography>
                  <Typography
                    sx={{
                      color: "text.secondary",
                      fontSize: "1.1rem",
                      lineHeight: 1.7,
                    }}
                  >
                    We combine deep industry expertise with cutting-edge
                    technology to create solutions that are powerful yet
                    intuitive, comprehensive yet simple to use.
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gap: 3,
                  }}
                >
                  {[
                    {
                      title: "Industry Expertise",
                      description:
                        "Our team brings decades of combined experience in restaurant operations and technology.",
                      icon: "restaurant",
                    },
                    {
                      title: "Customer-Centric",
                      description:
                        "We actively listen to our users and continuously evolve our platform based on their needs.",
                      icon: "people",
                    },
                    {
                      title: "Innovation Focus",
                      description:
                        "Constantly pushing boundaries with AI and automation to solve complex challenges.",
                      icon: "lightbulb",
                    },
                  ].map((item, index) => (
                    <Box
                      key={item.title}
                      sx={{
                        p: 3,
                        borderRadius: "16px",
                        border: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        gap: 3,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          borderColor: "primary.main",
                          transform: "translateY(-4px)",
                          boxShadow: "0 12px 24px rgba(0, 0, 0, 0.05)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "12px",
                          background:
                            "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ color: "white" }}>{item.icon}</Icon>
                      </Box>
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            mb: 1,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          sx={{
                            color: "text.secondary",
                            fontSize: "1rem",
                            lineHeight: 1.6,
                          }}
                        >
                          {item.description}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Journey Section */}
      <Box
        sx={{
          py: { xs: 10, md: 16 },
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)"
              : "linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)",
          position: "relative",
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h2"
              align="center"
              sx={{
                fontSize: { xs: "2rem", md: "3rem" },
                fontWeight: 800,
                mb: 2,
                background: "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Our Journey of Innovation
            </Typography>
            <Typography
              align="center"
              sx={{
                color: "text.secondary",
                fontSize: { xs: "1.1rem", md: "1.25rem" },
                lineHeight: 1.7,
                mb: 12,
                maxWidth: "800px",
                mx: "auto",
              }}
            >
              From concept to reality, we're building the future of restaurant
              management through continuous innovation and improvement.
            </Typography>
          </motion.div>
          <Box sx={{ maxWidth: "900px", mx: "auto" }}>
            {milestones.map((milestone, index) => (
              <TimelineItem
                key={milestone.phase}
                {...milestone}
                index={index}
              />
            ))}
          </Box>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box
        sx={{
          py: { xs: 10, md: 16 },
          background: "linear-gradient(135deg, #000B18 0%, #001E3C 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.4,
            background:
              "radial-gradient(circle at 20% 20%, rgba(6, 182, 212, 0.15) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgba(59, 130, 246, 0.15) 0%, transparent 40%)",
            zIndex: 0,
          }}
        />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h2"
              align="center"
              sx={{
                fontSize: { xs: "2rem", md: "3.5rem" },
                fontWeight: 800,
                mb: 3,
                color: "white",
                lineHeight: 1.2,
              }}
            >
              Ready to Transform Your Restaurant?
            </Typography>
            <Typography
              align="center"
              sx={{
                color: "rgba(255, 255, 255, 0.8)",
                fontSize: { xs: "1.1rem", md: "1.25rem" },
                lineHeight: 1.7,
                mb: 6,
                maxWidth: "800px",
                mx: "auto",
              }}
            >
              Join hundreds of successful restaurants already using VenFlows to
              streamline their operations.
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 3,
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate("/auth/register")}
                sx={{
                  background: "linear-gradient(90deg, #06B6D4, #0891B2)",
                  color: "white",
                  px: 4,
                  py: 2,
                  fontSize: "1.1rem",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: "0 8px 24px rgba(6, 182, 212, 0.25)",
                  "&:hover": {
                    background: "linear-gradient(90deg, #0891B2, #06B6D4)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 32px rgba(6, 182, 212, 0.35)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Get Started Now
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate("/contact")}
                sx={{
                  px: 4,
                  py: 2,
                  fontSize: "1.1rem",
                  borderColor: "rgba(255, 255, 255, 0.3)",
                  color: "white",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "#06B6D4",
                    background: "rgba(6, 182, 212, 0.1)",
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Talk to Sales
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};
